import React from 'react';
import { DialogContentText } from '@mui/material';

import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';

export type ChangeTicketAction = 'approve' | 'reject' | 'revert';

interface ChangeTicketConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  action: ChangeTicketAction;
  vendorName: string;
  standardVendorName?: string;
  isExistingVendor?: boolean;
}

interface ActionConfig {
  title: string;
  getMessage: (vendorName: string, standardVendorName?: string, isExistingVendor?: boolean) => string;
  buttonText: string;
  cypressId: string;
}

const ACTION_CONFIG: Record<ChangeTicketAction, ActionConfig> = {
  approve: {
    title: 'Approve Change Ticket',
    getMessage: (vendorName, standardVendorName, isExistingVendor) => {
      if (isExistingVendor && standardVendorName) {
        return `Are you sure you want to add "${vendorName}" as an alias to vendor "${standardVendorName}"?`;
      }
      return `Are you sure you want to create a new vendor with name "${standardVendorName || vendorName}"?`;
    },
    buttonText: 'Approve',
    cypressId: 'dialog-approve-change-ticket',
  },
  reject: {
    title: 'Reject Change Ticket',
    getMessage: (vendorName) =>
      `Are you sure you want to reject the change ticket for vendor "${vendorName}"?`,
    buttonText: 'Reject',
    cypressId: 'dialog-reject-change-ticket',
  },
  revert: {
    title: 'Revert Rejection',
    getMessage: (vendorName) =>
      `Are you sure you want to revert the rejection for vendor "${vendorName}"? This will return the ticket to pending status.`,
    buttonText: 'Revert',
    cypressId: 'dialog-revert-rejection',
  },
};

const ChangeTicketConfirmDialog: React.FC<ChangeTicketConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  action,
  vendorName,
  standardVendorName,
  isExistingVendor,
}) => {
  const config = ACTION_CONFIG[action];

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading) {
      onConfirm();
    }
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      loading={isLoading}
      cypressId={config.cypressId}
    >
      <div onKeyPress={handleKeyPress}>
        <DialogTitleGlynt title={config.title} closeModal={onClose} loading={isLoading} />
        <DialogContentGlynt>
          <DialogContentText>{config.getMessage(vendorName, standardVendorName, isExistingVendor)}</DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={onConfirm}
          closeModal={onClose}
          cancelCypressId={`btn-${action}-change-ticket-modal-cancel`}
          submitCypressId={`btn-${action}-change-ticket-modal-yes`}
          clearCypressId=""
          submitButtonText={config.buttonText}
          submitLoading={isLoading}
          showClearButton={false}
        />
      </div>
    </DialogGlynt>
  );
};

export default ChangeTicketConfirmDialog;
